import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import React, { useState } from 'react'
import styled from 'styled-components'
import { TextWidthExtended } from '@ta-interaktiv/react-disco-grid'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { Button } from '@mui/material'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import CloseIcon from '@mui/icons-material/Close'

import { getMuiTheme } from './theme'
import { device } from '../breakpoints'
import Map from '../Map'
import Controls from '../Controls'
import TrendIndicator from '../TrendIndicator'
import { GlobalVariables } from 'Globals/GlobalVariables'

declare global {
  interface Window {
    dataLayer: any[]
  }
}

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null)
  const [searchInput, setSearchInput] = useState<string | null>(null)
  const [propertyType, setPropertyType] = useState<'appartement' | 'maison'>(
    'maison'
  )
  const [roomCount, setRoomCount] = useState<
    '' | '1' | '2' | '3' | '4' | '5' | '6'
  >('')
  // Map navigation switch
  const [mapIsLocked, setMapIsLocked] = useState(true)
  const [municipalityNames, setMunicipalityNames] = useState<string[]>([])
  const isMobile = window.innerWidth < 768
  const muiTheme = getMuiTheme(ref, colorMode, tenant.key || 'tdg')
  const dataVersionDate = properties?.dataVersion
    ? properties?.dataVersion
    : '2023-09-01' // '2024-07-01'
  const department =
    (properties?.departement as 'ain' | 'haute-savoie') || 'haute-savoie'

  const dataVersions: Dictionary = {
    '2023-09-01': {
      date: 'septembre 2023',
      file: '2023-09-01',
      meanPrices: {
        ain: {
          appartement: 2474,
          maison: 2553,
        },
        'haute-savoie': {
          appartement: 4528,
          maison: 4846,
        },
      },
    },
    '2024-01-01': {
      date: 'janvier 2024',
      file: '2024-01-01',
      meanPrices: {
        ain: {
          appartement: 2416,
          maison: 2566,
        },
        'haute-savoie': {
          appartement: 4555,
          maison: 5005,
        },
      },
      trends: {
        ain: {
          appt_perc_change: -2.4,
          appt_change_cat: '↘',
          house_perc_change: 0.5,
          house_change_cat: '↗',
        },
        'haute-savoie': {
          appt_perc_change: 0.6,
          appt_change_cat: '↗',
          house_perc_change: 3.18,
          house_change_cat: '↗',
        },
        text: 'depuis sept. 2023',
      },
    },
    '2024-07-01': {
      date: 'juillet 2024',
      file: '2024-07-01',
      meanPrices: {
        ain: { appartement: 2344, maison: 2401 },
        'haute-savoie': { appartement: 4318, maison: 4643 },
      },
      trends: {
        ain: {
          appt_perc_change: -3.0,
          appt_change_cat: '↘',
          house_perc_change: -6.4,
          house_change_cat: '↘',
        },
        'haute-savoie': {
          appt_perc_change: -5.2,
          appt_change_cat: '↘',
          house_perc_change: -7.2,
          house_change_cat: '↘',
        },
        text: 'depuis jan. 2024',
      },
    },
  }

  const triggerSearch = (search: string) => {
    setSearchInput(search)
    // @ts-ignore
    if (ref) {
      ref.scrollIntoView()
    }
  }
  const triggerPropertyTypeChange = (propertyType: string) => {
    setPropertyType(propertyType as 'appartement' | 'maison')
  }
  const triggerRoomChange = (room: string) => {
    setRoomCount(room as '' | '1' | '2' | '3' | '4' | '5' | '6')
  }
  const handleMunicipalityNames = (municipalityNames: string[]) => {
    setMunicipalityNames(municipalityNames)
  }
  return (
    <AppContainer $colorMode={colorMode} ref={setRef}>
      <TextWidthExtended>
        {properties?.title ? (
          <Title>{properties.title}</Title>
        ) : department === 'haute-savoie' ? (
          <Title>Département de la Haute-Savoie</Title>
        ) : (
          <>
            <Title>Département de l’Ain</Title>
          </>
        )}
        <Lead>
          <p>
            Prix moyens, en euros et au m<sup>2</sup>, dans l’ancien (existant),
            au 1<sup>er</sup> {dataVersions[dataVersionDate].date}:
          </p>
          <ul>
            <li>
              <b>
                {
                  dataVersions[dataVersionDate].meanPrices[department]
                    .appartement
                }
              </b>{' '}
              pour un appartement{' '}
              {dataVersions[dataVersionDate].trends && (
                <TrendIndicator
                  perc_change={
                    dataVersions[dataVersionDate].trends[department]
                      .appt_perc_change
                  }
                  arrow={
                    dataVersions[dataVersionDate].trends[department]
                      .appt_change_cat
                  }
                  text={dataVersions[dataVersionDate].trends.text}
                />
              )}
            </li>
            <li>
              <b>
                {dataVersions[dataVersionDate].meanPrices[department].maison}
              </b>{' '}
              pour une maison{' '}
              {dataVersions[dataVersionDate].trends && (
                <TrendIndicator
                  perc_change={
                    dataVersions[dataVersionDate].trends[department]
                      .house_perc_change
                  }
                  arrow={
                    dataVersions[dataVersionDate].trends[department]
                      .house_change_cat
                  }
                  text={dataVersions[dataVersionDate].trends.text}
                />
              )}
            </li>
          </ul>
        </Lead>

        <MuiThemeProvider theme={muiTheme}>
          <Controls
            triggerSearch={triggerSearch}
            triggerPropertyTypeChange={triggerPropertyTypeChange}
            triggerRoomChange={triggerRoomChange}
            municipalityNames={municipalityNames}
          />
        </MuiThemeProvider>
        <MapContainer>
          <MuiThemeProvider theme={muiTheme}>
            <MapActivationButtonContainer
              onClick={() => {
                setMapIsLocked(!mapIsLocked)
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                  event: 'Interactions',
                  event_action: 'click',
                  event_label: `mapactivationbutton`,
                })
              }}
            >
              <Button
                disableElevation
                variant='contained'
                startIcon={mapIsLocked ? <ZoomOutMapIcon /> : <CloseIcon />}
              >
                {mapIsLocked
                  ? isMobile
                    ? 'Naviguer'
                    : 'Naviguer dans la carte'
                  : isMobile
                  ? 'Quitter'
                  : 'Quitter la navigation'}
              </Button>
            </MapActivationButtonContainer>

            <Map
              department={department}
              dataVersion={dataVersions[dataVersionDate]}
              propertyType={propertyType}
              roomCount={roomCount}
              searchInput={searchInput}
              locked={mapIsLocked}
              getMunicipalityNames={handleMunicipalityNames}
              useStaticThresholds={properties?.useStaticThresholds === 'true'}
            />
          </MuiThemeProvider>
        </MapContainer>
        <Note>
          Note: en France, un deux-pièces équivaut à une chambre et un salon, un
          trois-pièces à deux chambres et un salon, etc. Source: Fnaim/LABEL
          (Laboratoire économique du logement).
        </Note>
      </TextWidthExtended>
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  margin-bottom: 2rem;
`
const Title = styled.h4`
  margin: 2rem 0 0.5rem 0;
  border-top: 4px solid #000;
`
const Lead = styled.div`
  font-size: 1.6rem;
  line-height: 1.4;
  p,
  ul {
    margin: 0;
  }
  margin-bottom: 1rem;
`
const MapContainer = styled.div``
const Note = styled.p`
  font-size: 1.4rem;
  line-height: 1.4;
`
const MapActivationButtonContainer = styled.div`
  position: relative;
  height: 0;
  right: 0;
  top: 0;
  width: 100%;
  text-align: right;
  z-index: 10;
  cursor: pointer;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    top: 200px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    top: 5px;
    display: block;
  }
`
export default MainComponent
